import {
    Form,
    FormPayload,
    Newsletter,
    NewsletterPayload,
    PreviewPageType,
    Template,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { EditorMultiSignUpEditMessage, EditorSaveData, EditorSaveError } from '@dev/template-editor';
import { ApplicationState } from '..';
import {
    CancelEditStepAction,
    CloseAction,
    CloseGalleryAction,
    CloseUpsellingOverlayAction,
    EditorFile,
    FilesType,
    PaidNewsletterCreatorActionTypes,
    PaidNewsletterCreatorState,
    PaidNewsletterErrors,
    PaidNewsletterMetaData,
    LoadErrorAction,
    LoadPageTemplateErrorAction,
    LoadPageTemplateStartAction,
    LoadPageTemplateSuccessAction,
    LoadStartAction,
    LoadSuccessAction,
    ModifyPaidNewsletterAction,
    ModifyPaidNewsletterMetaDataAction,
    OpenGalleryAction,
    OpenUpsellingOverlayAction,
    ResetAction,
    SaveAllFormsErrorAction,
    SaveAllFormsSuccessAction,
    SaveErrorAction,
    SaveStartAction,
    SaveSuccessAction,
    SetIsTemplateOpenAction,
    SetPaidNewsletterErrorsAction,
    SetTemplatePreviewAction,
    StartEditStepAction,
    TemplateChangedAction,
    TemplateDeselectAction,
    TemplateFormAddLoadedAction,
    TemplateFormAddNewAction,
    TemplateFormDuplicatedAction,
    TemplateFormErrorNames,
    TemplateFormsFromReplacedTemplateClearAction,
    TemplateFormPayloadUpdateAction,
    TemplateFormsCheckInvalidAction,
    TemplateFormSetErrorsAction,
    TemplateFormSetMailingPreferencesLabelAction,
    TemplateFormsRemoveAction,
    TemplateFormsSetDefaultDoubleOptInDisabledAction,
    TemplateFormsSetInvalidAction,
    TemplateFormUpdateSavedAction,
    TemplateIncrementAutosaveCounterAction,
    TemplateLoadSingleErrorAction,
    TemplateLoadSingleStartAction,
    TemplateLoadSingleSuccessAction,
    TemplateOpenFromGalleryAction,
    TemplatePreviewFromGalleryAction,
    TemplateShowSaveErrorsAction,
    TemplateUploadFileAction,
    TemplateUploadFileErrorAction,
    TemplateUploadFileStartAction,
    TemplateUploadFileSuccessAction,
    ModifyEmailTemplateAction,
    SetUrlPayloadAction,
    SaveEmailTemplateStartAction,
    SaveEmailTemplateSuccessAction,
    SaveEmailTemplateErrorAction,
    PostPurchaseTemplatePayload,
    TemplateSetFromGalleryAction,
    TemplateSetMultiSignUpEditMessageAction,
    TemplatesSaveSettings,
    UpdateTemplatesSaveSettingsAction,
    PublishPaidNewsletterStartAction,
    PublishPaidNewsletterErrorAction,
    PaidNewsletterErrorNames,
    SubscriptionCancelationTemplatePayload,
    ModifySubscriptionCancelationTemplateAction,
    SaveSubscriptionCancelationTemplateSuccessAction,
    SaveSubscriptionCancelationTemplateErrorAction,
    SaveSubscriptionCancelationTemplateStartAction,
    EmailTemplateType,
    SetEmailTemplateOpenTypeAction
} from './types';

export const LoadStart = (name: string): LoadStartAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_START,
    payload: { name },
});

export const LoadSuccess = (
    paidNewsletterObject: Newsletter
): LoadSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_SUCCESS,
    payload: { paidNewsletterObject },
});

export const LoadError = (): LoadErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_ERROR,
});

export const LoadPageTemplateStart = (pageTemplateType: PreviewPageType): LoadPageTemplateStartAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_START,
    payload: { pageTemplateType },
});

export const LoadPageTemplateSuccess = (
    pageTemplateType: PreviewPageType,
    pageTemplate: string
): LoadPageTemplateSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_SUCCESS,
    payload: { pageTemplateType, pageTemplate },
});

export const LoadPagePreviewError = (): LoadPageTemplateErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.LOAD_PAGE_TEMPLATE_ERROR,
});

export const SaveStart = ({
    data,
    exitOnSave,
    errors,
    isSaveBlocked,
    errorsToCheck
}: {
    data?: EditorSaveData;
    exitOnSave?: boolean;
    errors?: EditorSaveError[];
    isSaveBlocked?: boolean;
    errorsToCheck?: PaidNewsletterErrorNames[]
}): SaveStartAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_START,
    payload: { data, exitOnSave, errors, isSaveBlocked, errorsToCheck },
    meta: {
        thunk: true,
    },
});


export const SaveSuccess = (
    paidNewsletterObject: Newsletter,
    editorSaveData?: EditorSaveData,
    paidNewsletterMetaDataObject?: PaidNewsletterMetaData,
    meta?: Record<string, unknown>
): SaveSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_SUCCESS,
    payload: { paidNewsletterObject, editorSaveData, paidNewsletterMetaDataObject },
    meta,
});
export const SaveAllFormsSuccess = (meta?: Record<string, unknown>): SaveAllFormsSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_SUCCESS,
    meta,
});

export const SaveError = (meta?: Record<string, unknown>): SaveErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_ERROR,
    meta,
    error: true,
});
export const SaveAllFormsError = (meta?: Record<string, unknown>): SaveAllFormsErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SAVE_ALL_ERROR,
    meta,
    error: true,
});

export const SaveEmailTemplateStart = ({
    data,
    exitOnSave,
}: {
    data?: EditorSaveData;
    exitOnSave?: boolean;
}): SaveEmailTemplateStartAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_START,
    payload: { data, exitOnSave },
    meta: {
        thunk: true,
    },
});


export const SaveEmailTemplateSuccess = (
    editorSaveData: EditorSaveData,
    savedEmailTemplate: Template,
    meta: Record<string, unknown>
): SaveEmailTemplateSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_SUCCESS,
    payload: { editorSaveData, savedEmailTemplate },
    meta,
});

export const SaveEmailTemplateError = (meta?: Record<string, unknown>): SaveEmailTemplateErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_EMAIL_TEMPLATE_ERROR,
    meta,
    error: true,
});

export const SaveSubscriptionCancelationTemplateStart = ({
    data,
    exitOnSave,
}: {
    data?: EditorSaveData;
    exitOnSave?: boolean;
}): SaveSubscriptionCancelationTemplateStartAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_START,
    payload: { data, exitOnSave },
    meta: {
        thunk: true,
    },
});

export const SaveSubscriptionCancelationTemplateSuccess = (
    editorSaveData: EditorSaveData,
    savedSubscriptionCancelationTemplate: Template,
    meta: Record<string, unknown>
): SaveSubscriptionCancelationTemplateSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_SUCCESS,
    payload: { editorSaveData, savedSubscriptionCancelationTemplate },
    meta,
});

export const SaveSubscriptionCancelationTemplateError = (meta?: Record<string, unknown>): SaveSubscriptionCancelationTemplateErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.SAVE_SUBSCRIPTION_CANCELATION_TEMPLATE_ERROR,
    meta,
    error: true,
});

export const ModifyPaidNewsletter = (
    paidNewsletterPayload: NewsletterPayload
): ModifyPaidNewsletterAction => ({
    type: PaidNewsletterCreatorActionTypes.MODIFY_PAID_NEWSLETTER,
    payload: {
        paidNewsletterPayload,
    },
});

export const ModifyPaidNewsletterMetaData = (
    paidNewsletterMetaDataPayload: PaidNewsletterMetaData
): ModifyPaidNewsletterMetaDataAction => ({
    type: PaidNewsletterCreatorActionTypes.MODIFY_PAID_NEWSLETTER_METADATA,
    payload: {
        paidNewsletterMetaDataPayload,
    },
});

export const StartEditStep = (stepInEdit: number): StartEditStepAction => ({
    type: PaidNewsletterCreatorActionTypes.START_EDIT_STEP,
    payload: { stepInEdit },
});

export const CancelEditStep = (): CancelEditStepAction => ({
    type: PaidNewsletterCreatorActionTypes.CANCEL_EDIT_STEP,
});

export const Close = (): CloseAction => ({
    type: PaidNewsletterCreatorActionTypes.CLOSE,
});

export const TemplateUploadFile = (file: File, blockId: string): TemplateUploadFileAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE,
    payload: {
        file,
        blockId,
    },
});

export const TemplateUploadFileStart = (): TemplateUploadFileStartAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_START,
});

export const TemplateUploadFileSuccess = (filesList: EditorFile[]): TemplateUploadFileSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_SUCCESS,
    payload: {
        filesList,
    },
});

export const TemplateUploadFileError = (): TemplateUploadFileErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_UPLOAD_FILE_ERROR,
});

export const TemplateIncrementAutosaveCounter = (): TemplateIncrementAutosaveCounterAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_INCREMENT_AUTOSAVE_COUNTER,
});

export const TemplateFormAddNew = (blockClientId: string): TemplateFormAddNewAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_ADD_NEW,
    payload: {
        blockClientId,
    },
});

export const TemplateFormsRemove = (formsBlockClientIds: string[]): TemplateFormsRemoveAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_REMOVE,
    payload: {
        formsBlockClientIds,
    },
});

export const TemplateFormsFromReplacedTemplateClear = (): TemplateFormsFromReplacedTemplateClearAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_FROM_REPLACED_TEMPLATE_CLEAR,
});

export const TemplateFormUpdateSaved = (blockClientId: string, form: Form): TemplateFormUpdateSavedAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_UPDATE_SAVED,
    payload: {
        blockClientId,
        form,
    },
});

export const TemplateFormAddLoaded = (
    blockClientId: string,
    form: Form,
    formMailingPreferencesLabel?: string
): TemplateFormAddLoadedAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_ADD_LOADED,
    payload: {
        blockClientId,
        form,
        formMailingPreferencesLabel,
    },
});

export const TemplateFormSetMailingPreferencesLabel = (
    blockClientId: string,
    formMailingPreferencesLabel?: string
): TemplateFormSetMailingPreferencesLabelAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SET_MAILING_PREFERENCES_LABEL,
    payload: {
        blockClientId,
        formMailingPreferencesLabel,
    },
});

export const TemplateFormDuplicated = (
    blockClientId: string,
    newBlockClientId: string
): TemplateFormDuplicatedAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_DUPLICATED,
    payload: {
        blockClientId,
        newBlockClientId,
    },
});

export const TemplateFormPayloadUpdate = (
    blockClientId: string,
    formPayload: Partial<FormPayload>
): TemplateFormPayloadUpdateAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_PAYLOAD_UPDATE,
    payload: {
        blockClientId,
        formPayload,
    },
});

export const TemplateFormsCheckInvalid = (blockClientIds?: string[]): TemplateFormsCheckInvalidAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_CHECK_INVALID,
    payload: {
        blockClientIds,
    },
});

export const TemplateFormsSetInvalid = (blockClientIds: string[]): TemplateFormsSetInvalidAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_SET_INVALID,
    payload: {
        blockClientIds,
    },
});

export const TemplateFormsSetDefaultDoubleOptInDisabled = (
    isDoubleOptInDisabledByDefault: boolean
): TemplateFormsSetDefaultDoubleOptInDisabledAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORMS_SET_DEFAULT_DOUBLE_OPT_IN,
    payload: {
        isDoubleOptInDisabledByDefault,
    },
});

export const TemplateFormSetErrors = (
    errors: Partial<Record<TemplateFormErrorNames, boolean>>,
    blockClientId: string
): TemplateFormSetErrorsAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_FORM_SET_ERRORS,
    payload: {
        errors,
        blockClientId,
    },
});

export const TemplateSetMultiSignUpEditMessage = (
    editMessage?: EditorMultiSignUpEditMessage
): TemplateSetMultiSignUpEditMessageAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SET_MUTLI_SIGN_UP_EDIT_MESSAGE,
    payload: {
        editMessage
    },
});

export const SetIsTemplateOpen = (isOpen: boolean): SetIsTemplateOpenAction => ({
    type: PaidNewsletterCreatorActionTypes.SET_IS_TEMPLATE_OPEN,
    payload: { isOpen },
});

export const SetEmailTemplateOpenType = (emailTemplateType: EmailTemplateType): SetEmailTemplateOpenTypeAction => ({
    type: PaidNewsletterCreatorActionTypes.SET_EMAIL_TEMPLATE_OPEN_TYPE,
    payload: { emailTemplateType },
});

export const SetTemplatePreview = (content?: string, name?: string, isPublic?: boolean): SetTemplatePreviewAction => ({
    type: PaidNewsletterCreatorActionTypes.SET_TEMPLATE_PREVIEW,
    payload: { content, name, isPublic },
});

export const Reset = (): ResetAction => ({
    type: PaidNewsletterCreatorActionTypes.RESET,
});

export const TemplateShowSaveErrors = (): TemplateShowSaveErrorsAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS,
});

export const TemplateChanged = (): TemplateChangedAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_CHANGED,
});

export const TemplateDeselect = (): TemplateDeselectAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_DESELECT,
});

export const OpenUpsellingOverlay = (feature: AvailableFeaturesUpselling): OpenUpsellingOverlayAction => ({
    type: PaidNewsletterCreatorActionTypes.OPEN_UPSELLING_OVERLAY,
    payload: {
        feature,
    },
});

export const CloseUpsellingOverlay = (): CloseUpsellingOverlayAction => ({
    type: PaidNewsletterCreatorActionTypes.CLOSE_UPSELLING_OVERLAY,
});

export const OpenGallery = (): OpenGalleryAction => ({
    type: PaidNewsletterCreatorActionTypes.OPEN_GALLERY,
});

export const CloseGallery = (): CloseGalleryAction => ({
    type: PaidNewsletterCreatorActionTypes.CLOSE_GALLERY,
});

export const TemplatePreviewFromGallery = (
    templateName?: string,
    isPublic?: boolean,
    templateType?: TemplateType
): TemplatePreviewFromGalleryAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY,
    payload: {
        templateName,
        isPublic,
        templateType
    },
});

export const TemplateOpenFromGallery = ({
    productTemplateName,
    confirmationTemplateName,
    isPublic
} : {  productTemplateName?: string, confirmationTemplateName?: string, isPublic?: boolean}): TemplateOpenFromGalleryAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY,
    payload: {
        productTemplateName,
        confirmationTemplateName,
        isPublic
    },
});

export const TemplateSetFromGallery = (productTemplate: Template, confirmationTemplate: Template): TemplateSetFromGalleryAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_SET_FROM_GALLERY,
    payload: {
        productTemplate,
        confirmationTemplate
    },
});

export const TemplateLoadSingleStart = (templateName?: string, isPublic?: boolean): TemplateLoadSingleStartAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_START,
    payload: {
        templateName,
        isPublic,
    },
    meta: {
        thunk: true,
    },
});

export const TemplateLoadSingleSuccess = (
    template: Template,
    meta?: Record<string, unknown>
): TemplateLoadSingleSuccessAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_SUCCESS,
    payload: { template },
    meta,
});

export const TemplateLoadSingleError = (meta?: Record<string, unknown>): TemplateLoadSingleErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.TEMPLATE_LOAD_SINGLE_ERROR,
    meta,
    error: true,
});

export const ModifyEmailTemplate = (payload: Partial<PostPurchaseTemplatePayload>): ModifyEmailTemplateAction => ({
    type: PaidNewsletterCreatorActionTypes.MODIFY_EMAIL_TEMPLATE,
    payload,
});

export const ModifySubscriptionCancelationTemplate = (payload: Partial<SubscriptionCancelationTemplatePayload>): ModifySubscriptionCancelationTemplateAction => ({
    type: PaidNewsletterCreatorActionTypes.MODIFY_SUBSCRIPTION_CANCELATION_TEMPLATE,
    payload,
});

export const SetPaidNewsletterErrors = (errors?: PaidNewsletterErrors): SetPaidNewsletterErrorsAction => ({
    type: PaidNewsletterCreatorActionTypes.SET_PAID_NEWSLETTER_ERRORS,
    payload: {
        errors,
    },
});

export const UpdateTemplatesSaveSettings = (settings: TemplatesSaveSettings): UpdateTemplatesSaveSettingsAction => ({
    type: PaidNewsletterCreatorActionTypes.UPDATE_TEMPLATES_SAVE_SETTINGS,
    payload: settings,
});

export const SetUrlPayload = (urlSuffix: string): SetUrlPayloadAction => ({
    type: PaidNewsletterCreatorActionTypes.SET_URL_PAYLOAD,
    payload: { urlSuffix },
});

export const PublishPaidNewsletterStart = (): PublishPaidNewsletterStartAction => ({
    type: PaidNewsletterCreatorActionTypes.PUBLISH_PAID_NEWSLETTER_START,
});

export const PublishPaidNewsletterError = (): PublishPaidNewsletterErrorAction => ({
    type: PaidNewsletterCreatorActionTypes.PUBLISH_PAID_NEWSLETTER_ERROR,
});

export const getPaidNewsletterCreatorStore = (store: ApplicationState): PaidNewsletterCreatorState =>
    store.paidNewsletterCreator;

export const getPaidNewsletterCreatorTemplateFilesStore = (store: ApplicationState): FilesType =>
    store.paidNewsletterCreator.template.files;

export const getPaidNewsletterCreatorGalleryOpenStore = (state: ApplicationState): boolean =>
    state?.paidNewsletterCreator?.isGalleryOpen;
